/**
 * Platform detection for ordering social-login buttons.
 *
 * Apple's Human Interface Guidelines say Sign in with Apple should be at least
 * as prominent as other sign-in options on Apple devices. We therefore show
 * the Apple button first on iOS and the Google button first on Android. On
 * everything else (desktop, other mobile) we keep a documented default order:
 * Google first, then Apple.
 */

export type Platform = "ios" | "android" | "other";

export type SocialProvider = "google" | "apple";

const isAndroid = (ua: string): boolean => /android/.test(ua);

const isAppleMobileUa = (ua: string): boolean => /iphone|ipod|ipad/.test(ua);

// iPadOS 13+ masquerades as desktop Safari ("Macintosh") but is the only
// "Mac" that reports multiple touch points.
const isTouchIpadOs = (ua: string, maxTouchPoints: number): boolean =>
  /macintosh/.test(ua) && maxTouchPoints > 1;

const isIos = (ua: string, maxTouchPoints: number): boolean =>
  isAppleMobileUa(ua) || isTouchIpadOs(ua, maxTouchPoints);

const defaultUserAgent = (): string =>
  typeof navigator !== "undefined" ? navigator.userAgent : "";

const defaultMaxTouchPoints = (): number =>
  typeof navigator !== "undefined" ? navigator.maxTouchPoints ?? 0 : 0;

/**
 * Detect the current platform from the user agent (and, for modern iPadOS,
 * the touch-capable "Macintosh" heuristic).
 *
 * @param userAgent - Override for the UA string (defaults to the live one).
 *   Supplying it keeps the function pure and testable.
 * @param maxTouchPoints - Override for `navigator.maxTouchPoints`.
 * @returns The detected platform bucket.
 */
export const getPlatform = (
  userAgent: string = defaultUserAgent(),
  maxTouchPoints: number = defaultMaxTouchPoints(),
): Platform => {
  const ua = userAgent.toLowerCase();

  if (isAndroid(ua)) {
    return "android";
  }
  if (isIos(ua, maxTouchPoints)) {
    return "ios";
  }
  return "other";
};

/**
 * Return the social providers in the order they should be rendered for the
 * given platform.
 *
 * - iOS: Apple first, then Google.
 * - Android: Google first, then Apple.
 * - Other/desktop: Google first, then Apple (documented default).
 *
 * @param platform - The detected platform; defaults to live detection.
 * @returns The ordered provider list.
 */
export const getSocialLoginOrder = (platform: Platform = getPlatform()): SocialProvider[] => {
  if (platform === "ios") {
    return ["apple", "google"];
  }
  return ["google", "apple"];
};
